/* assets/club-print-mini-editor.js

   Version: 2.7.0

   CHANGES v2.7.0:
   ✅ E15: XSS fix — _setHint uses textContent; _updateMissingFieldsHint sets innerHTML directly
   ✅ E4:  Unified key separator to '__' throughout (was mixed ::, _, __)
   ✅ E1:  Memoised _buildRowsFromBulk via _getCachedRows()
   ✅ E2:  _collectSelections() called once per render loop; passed into _fillRowInputs
   ✅ E3:  Removed duplicate esc() definition in _formatRowTitleFromLineItem()
   ✅ E5:  File objects stored in _fileStore to survive row rebuilds
   ✅ E6:  Removed 150 ms artificial delay between service item POSTs
   ✅ E7:  Replaced nested setTimeouts with sequential calls after _refreshCartSections
   ✅ E8:  Deleted dead methods _removeAllProductItems, _removeServiceItems
   ✅ E9:  _buildVariantTableJSON_PrintEditor returns raw object; inspectPayload uses tableData
   ✅ E10: Fixed _warn() method
   ✅ E11: Independent maxShowGroups / maxShowRows dataset attributes
   ✅ E12: Replaced this.remove() with style.display = 'none'
   ✅ E13: Cached reset button; debounced _updateUIState with rAF in input/change handlers
   ✅ E14: Fixed _collapseGroups() double querySelector
   ✅ E16: Deleted dead methods _zonesI18ByGroupFromSelections, _zonesI18ForBucket,
           _propNameForGroup, _textPropNameForGroup
   ✅ O4:  _formatPrice uses Intl.NumberFormat
   ✅ O7:  Consistent PubSub guard checks both publish and subscribe

   CHANGES v2.6.0:
   ✅ NEW: Boolean input type (checkbox yes/no) for once-groups and per-row
   ✅ NEW: Boolean is always "filled" — false is a valid value, no validation block
   ✅ NEW: Boolean resets to unchecked on reset()
   ✅ NEW: Boolean counted as "touched" only when checked (for reset-button visibility)

   CHANGES v2.5.0:
   ✅ NEW: Expandable groups with max-show limit
   ✅ NEW: Expandable rows with max-show limit
   ✅ NEW: Show more/less buttons with counters
   ✅ CLEANUP: Removed all comments for cleaner code
*/

(() => {
  const hasPub = () =>
    typeof window.publish === 'function' && typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS;

  const EVENTS = () => window.PUB_SUB_EVENTS || {};

  const jsonFetchConfig = (method = 'POST') => ({
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  });

  const fileToObjectURL = (file) => {
    if (!file) return '';
    try {
      return URL.createObjectURL(file);
    } catch {
      return '';
    }
  };

  class ClubPrintMiniEditor extends HTMLElement {
    connectedCallback() {
      this._busy = false;
      this._batchId = '';
      this._rows = [];
      this._bulkItems = [];
      this._abortController = null;
      this._cachedElements = {};
      this._objectUrls = new Set();
      this._hintState = 'idle';

      this.debug = String(this.dataset.debug || 'false') === 'true';
      this.baseHandle = String(this.dataset.baseHandle || '').trim();
      this.clubCollection = {
        handle: this.dataset.collClubHdl || null,
        title: this.dataset.collClubTitle || null
      };
      this.openDrawer = String(this.dataset.openDrawer || 'true') === 'true';
      this.addServices = String(this.dataset.addServices || 'true') === 'true';
      this.removePrev = false;
      this.dryRun = String(this.dataset.dryRun || 'false') === 'true';

      this.maxShowGroups = parseInt(this.dataset.maxShowGroups ?? this.dataset.maxShow ?? '999', 10);
      this.maxShowRows   = parseInt(this.dataset.maxShowRows   ?? this.dataset.maxShow ?? '999', 10);
      this.fixedZonesCount = parseInt(this.dataset.fixedZonesCount || '0', 10);
      this._groupsExpanded = false;
      this._rowsExpanded = false;
      this._savedGroupState = new Map();
      this._deletedRowKeys = new Set();
      this._deletedFields = new Set();
      this._rowsCache = null;
      this._rowsCacheSource = null;
      this._fileStore = new Map();
      this._uiRaf = 0;

      const pick = (...keys) => {
        for (const k of keys) {
          const v = this.dataset?.[k];
          if (v != null && String(v).trim() !== '') return v;
        }
        return null;
      };

      this.i18n = {
        loading: pick('i18Loading', 'i18nLoading') || 'Loading…',
        errorCart: pick('i18CartError', 'i18nErrorCart') || 'Error loading cart',
        rowsSuffix: pick('i18RowsSuffix', 'i18nRowsSuffix') || 'rows',
        rowsEmpty: pick('i18RowsMetaEmpty', 'i18RowsEmpty', 'i18nRowsEmpty') || 'No items in cart',
        hintNoRows: pick('i18HintNoRows', 'i18nHintNoRows') || 'No items in cart.',
        hintNoSelections: pick('i18HintNoZones', 'i18HintNoSelections', 'i18nHintNoSelections') || 'Please select zones.',
        hintSaving: pick('i18HintSaving', 'i18nHintSaving') || 'Saving…',
        hintSaved: pick('i18HintSaved', 'i18nHintSaved') || 'Saved.',
        hintError: pick('i18HintSaveError', 'i18HintError', 'i18nHintError') || 'Error while saving.',
        rowEmpty: pick('i18RowEmpty', 'i18nRowEmpty') || 'Select zones above.',
        fileTooBig: pick('i18FileTooBig', 'i18nFileTooBig') || 'File too big (max 5MB)',
        fileInvalidType: pick('i18FileInvalidType', 'i18nFileInvalidType') || 'Invalid file type',
        fileChoose: pick('i18FileChoose', 'i18nFileChoose') || 'Datei auswählen',
        sharedRef: pick('i18SharedRef', 'i18nSharedRef') || 'Vereinslogo wird verwendet',
        currency: pick('i18Currency', 'i18nCurrency') || '€',
        showMore: pick('i18ShowMore', 'i18nShowMore') || 'Mehr anzeigen',
        showLess: pick('i18ShowLess', 'i18nShowLess') || 'Weniger anzeigen',
      };

      const raw = this.getAttribute('data-source') || '[]';
      try {
        this.data = JSON.parse(raw);
        if (!Array.isArray(this.data)) throw new Error('data-source must be an array');
      } catch (e) {
        this.data = [];
        this._warn('data-source JSON parse failed', e);
      }

      if (!this.baseHandle) {
        this._warn('missing baseHandle — hiding element');
        this.style.display = 'none';
        return;
      }

      this._noPrint = !this.data.length;
      if (this._noPrint) this.classList.add('cpm--no-print');

      this._scheduleUIUpdate = () => {
        cancelAnimationFrame(this._uiRaf);
        this._uiRaf = requestAnimationFrame(() => this._updateUIState());
      };

      this._renderShellFromTemplate();
      this._cacheElements();
      this._renderGroupsUnified();
      this._bindUI();
      this._setupBulkIntegration();


      this.syncFromCart(true);

      this._cartObserver = new MutationObserver(() => {
        this.syncFromCart(false);
      });

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        this._cartObserver.observe(drawer, {
          childList: true,
          subtree: true
        });
      }

      if (typeof window.subscribe === 'function' && typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        this._cartUpdateUnsub = subscribe(
          window.PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event?.source === 'club-print-mini-editor') return;
            this.syncFromCart(false);
          }
        );
      }
    }

    disconnectedCallback() {
      this._revokeAllObjectUrls();
      if (this._fileStore) this._fileStore.clear();
      if (this._abortController) this._abortController.abort();
      if (this._cartUpdateUnsub) {
        this._cartUpdateUnsub();
        this._cartUpdateUnsub = null;
      }
      if (this._cartObserver) this._cartObserver.disconnect();
      document.removeEventListener('bulk-payload-changed', this._bulkPayloadHandler);
    }

    _setupBulkIntegration() {
      this._bulkPayloadHandler = (e) => {
        this._bulkItems = e.detail.items || [];
        this._updateRowsFromBulk();
      };
      document.addEventListener('bulk-payload-changed', this._bulkPayloadHandler);
    }

    _updateRowsFromBulk() {
      this._rowsCache = null;
      if (!this._bulkItems.length) {
        this._rows = [];
        const rowsHost = this._el('rowsHost');
        const metaHost = this._el('rowsMeta');
        if (rowsHost) rowsHost.innerHTML = '';
        if (metaHost) metaHost.textContent = this.i18n.rowsEmpty;
        this._updatePriceSummary();
        this._clearHint();
        this._setStatus('');
        this._updateUIState();
        return;
      }
      if (this._hintState === 'warning') this._clearHint();
      this._renderRowsFromBulk(this._bulkItems);
      this._updatePriceSummary();
      this._setStatus('');
      this._updateUIState();
      this._updateMissingFieldsHint();
    }

    _renderRowsFromBulk(bulkItems) {
      const rowsHost = this._el('rowsHost');
      const metaHost = this._el('rowsMeta');
      if (!rowsHost) return;

      const rows = this._buildRowsFromBulk(bulkItems);
      this._rows = rows;

      const servicesEl = this._el('services') || this.querySelector('[data-role="services"]');
      if (servicesEl) {
        servicesEl.style.display = '';
      }

      if (metaHost) {
        metaHost.textContent = rows.length
          ? `${rows.length} ${this.i18n.rowsSuffix}`
          : this.i18n.rowsEmpty;
      }

      this._renderRowsWithExpansion(rows);
    }

    _renderRowsWithExpansion(rows) {
      const rowsHost = this._el('rowsHost');
      if (!rowsHost) return;

      // Persist current text/boolean values before wiping the DOM
      const savedValues = new Map();
      rowsHost.querySelectorAll('.cpm__row-field-input, .cpm__row-field-boolean').forEach(el => {
        const rk = el.dataset.rowKey;
        if (!rk) return;
        const mapKey = `${rk}__${el.dataset.group || ''}__${el.dataset.zone || ''}`;
        savedValues.set(mapKey, el.classList.contains('cpm__row-field-boolean')
          ? { boolean: true, checked: el.checked }
          : { boolean: false, value: el.value });
      });

      // E2: compute selections once for the entire loop
      const selections = this._collectSelections();

      const fragment = document.createDocumentFragment();

      if (!rows.length) {
        const empty = this._clone('emptyCart');
        if (empty) fragment.appendChild(empty);
      } else {
        // Expander and row numbering are based on active (non-deleted) rows only.
        const activeRows = rows.filter(r => !this._deletedRowKeys.has(r.key));
        const shouldShowExpander = activeRows.length > this.maxShowRows;
        const hiddenCount = activeRows.length - this.maxShowRows;
        const visibleRows = this._rowsExpanded ? rows : rows.slice(0, this.maxShowRows);

        let visibleNum = 0;
        visibleRows.forEach((r) => {
          const isDeleted = this._deletedRowKeys.has(r.key);
          if (!isDeleted) visibleNum++;

          const rowEl = this._clone('row');
          if (!rowEl) return;

          rowEl.dataset.key = r.key;
          rowEl.dataset.variantId = r.variant_id;

          const titleEl = rowEl.querySelector('[data-role="row-title"]');
          if (titleEl) {
            titleEl.innerHTML = `<span data-role="row-number">${visibleNum}.</span> ${r.title || ''}`;
          }

          const subEl = rowEl.querySelector('[data-role="row-sub"]');
          if (subEl) subEl.textContent = `#${r.key}`;

          const headerEl = rowEl.querySelector('.cpm__row-header');
          if (headerEl) {
            const delBtn = document.createElement('button');
            delBtn.type = 'button';
            delBtn.className = 'cpm__row-delete-btn';
            delBtn.dataset.action = 'delete-row';
            delBtn.dataset.rowKey = r.key;
            delBtn.title = 'Zeile entfernen';
            delBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
            headerEl.appendChild(delBtn);
          }

          const inputsHost = rowEl.querySelector('[data-role="row-inputs"]');
          if (inputsHost) this._fillRowInputs(inputsHost, r.key, selections);

          if (isDeleted) rowEl.style.display = 'none';

          fragment.appendChild(rowEl);
        });

        if (shouldShowExpander) {
          const expanderBottom = this._rowsExpanded
            ? this._createRowsCollapser()
            : this._createRowsExpander(hiddenCount);
          if (expanderBottom) fragment.appendChild(expanderBottom);
        }
      }

      rowsHost.innerHTML = '';
      rowsHost.appendChild(fragment);

      // Restore saved values into newly created inputs
      if (savedValues.size > 0) {
        rowsHost.querySelectorAll('.cpm__row-field-input, .cpm__row-field-boolean').forEach(el => {
          const rk = el.dataset.rowKey;
          if (!rk) return;
          const mapKey = `${rk}__${el.dataset.group || ''}__${el.dataset.zone || ''}`;
          const saved = savedValues.get(mapKey);
          if (!saved) return;
          if (saved.boolean && el.classList.contains('cpm__row-field-boolean')) {
            el.checked = saved.checked;
          } else if (!saved.boolean && el.classList.contains('cpm__row-field-input')) {
            el.value = saved.value;
          }
        });
      }

      // E5: Restore File objects from _fileStore into newly created file inputs
      if (this._fileStore && this._fileStore.size > 0) {
        rowsHost.querySelectorAll('input[type="file"]').forEach(input => {
          const fileKey = input.dataset.sharedGroup
            ? `once__${input.dataset.sharedGroup}`
            : `${input.dataset.rowKey || 'once'}__${input.dataset.group || ''}__${input.dataset.zone || ''}`;
          const storedFile = this._fileStore.get(fileKey);
          if (storedFile) this._updateFileUI(input, storedFile);
        });
      }
    }

    _createRowsExpander(hiddenCount) {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__rows-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--rows';
      button.dataset.action = 'expand-rows';
      button.innerHTML = `${this.i18n.showMore} (${hiddenCount})`;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _createRowsCollapser() {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__rows-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--rows';
      button.dataset.action = 'collapse-rows';
      button.innerHTML = this.i18n.showLess;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _buildRowsFromBulk(bulkItems) {
      const rows = [];
      let idx = 0;

      bulkItems.forEach((item) => {
        const vId = String(item.variant_id);
        const qty = Number(item.quantity);

        let title;
        const color = String(item.color || '').trim();
        const size = String(item.size || '').trim();
        const productTitle = String(item.product_title || '').trim();

        const hasColor = color && color !== 'Default Title';
        const hasSize = size && size !== 'Default Title';

        if (hasColor && hasSize) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${size}</span><span class="cpm__row-title-sep">-</span><span class="cpm__row-title-part">${color}</span>`;
        } else if (hasColor) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${color}</span>`;
        } else if (hasSize) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${size}</span>`;
        } else if (productTitle) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${productTitle}</span>`;
        } else {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">Artikel</span>`;
        }

        for (let i = 1; i <= qty; i++) {
          idx += 1;
          rows.push({
            key: `${vId}#${idx}`,
            variant_id: vId,
            title,
            bulk_item: item
          });
        }
      });

      return rows;
    }

    _getCachedRows() {
      if (!this._rowsCache || this._rowsCacheSource !== this._bulkItems) {
        this._rowsCache = this._buildRowsFromBulk(this._bulkItems);
        this._rowsCacheSource = this._bulkItems;
      }
      return this._rowsCache;
    }

    refresh() {
      return this.syncFromCart(false);
    }

    _tpl(name) {
      return this.querySelector(`template[data-tpl="${name}"]`) || null;
    }

    _clone(name) {
      const tpl = this._tpl(name);
      if (!tpl) return null;
      return tpl.content.firstElementChild?.cloneNode(true) || null;
    }

    _renderShellFromTemplate() {
      const shell = this._clone('shell');
      if (!shell) {
        this._warn('shell template missing');
        return;
      }

      const templates = Array.from(this.querySelectorAll('template[data-tpl]'));
      this.innerHTML = '';
      this.appendChild(shell);
      templates.forEach((t) => this.appendChild(t));
    }

    _cacheElements() {
      this._cachedElements = {
        status: this.querySelector('[data-role="status"]'),
        rowsHost: this.querySelector('[data-role="rows"]'),
        rowsMeta: this.querySelector('[data-role="rows-meta"]'),
        hint: this.querySelector('[data-role="hint"]'),
        services: this.querySelector('[data-role="services"]'),
        resetBtn: this.querySelector('[data-action="reset"]'),
      };
    }

    _el(key) {
      return this._cachedElements[key] || null;
    }

    _renderGroupsUnified() {
      const host = this._el('services');
      if (!host) return;

      host.innerHTML = '';

      const shouldShowExpander = this.data.length > this.maxShowGroups;
      const hiddenGroups = this.data.slice(this.maxShowGroups);

      this.data.forEach((g, idx) => {
        const group = String(g?.group || '').trim();
        if (!group) return;

        const label = String(g?.label || group);
        const type = String(g?.type || 'text').toLowerCase();
        const once = !!g?.once;

        const fileMode = String(g?.file_mode || 'upload').toLowerCase();
        const defaultValue = String(g?.default_value || '').trim();

        const zones = Array.isArray(g?.zones) ? g.zones : [];

        const groupEl = this._clone('groupUnified');
        if (!groupEl) return;

        groupEl.dataset.group = group;
        groupEl.dataset.type = type;
        groupEl.dataset.once = once ? 'true' : 'false';
        groupEl.dataset.zoneOnly = g?.zone_only ? 'true' : 'false';
        groupEl.dataset.zoneChecked = g?.zone_checked ? 'true' : 'false';

        groupEl.dataset.expanded = 'false';
        groupEl.dataset.open = 'false';

        if (type === 'file') {
          groupEl.dataset.fileMode = fileMode || 'upload';
        }
        if (defaultValue) groupEl.dataset.defaultValue = defaultValue;

        const labelEl = groupEl.querySelector('[data-role="group-label"]');
        if (labelEl) {
          labelEl.textContent = label;
        }

        const priceEl = groupEl.querySelector('[data-role="group-price"]');
        if (priceEl) {
          const price = Number(g?.product?.price || 0);
          const formattedPrice = this._formatPrice(price);
          priceEl.textContent = price > 0 ? `+${formattedPrice}` : formattedPrice;
        }

        const zonesHost = groupEl.querySelector('[data-role="zones"]');
        if (zonesHost) {
          zonesHost.innerHTML = '';

          zones.forEach((z) => {
            const zKey = String(z?.key || z?.raw || '').trim();
            if (!zKey) return;

            const zI18 = String(z?.i18 || '').trim();
            const zLabel = String(zI18 || z?.raw || zKey);

            const zoneEl = this._clone('zone');
            if (!zoneEl) return;

            const cb = zoneEl.querySelector('input.cpm__cb');
            if (cb) {
              cb.dataset.group = group;
              cb.dataset.zone = zKey;
              cb.dataset.zoneI18 = zI18;
              if (g?.zone_checked === true) {
                cb.checked = true;

                const lock = document.createElement('span');
                lock.className = 'cpm__zone-lock';
                lock.innerHTML = `
                  <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M666 486v-76.4c0-84.8-69.2-153.6-154-153.6s-154 68.8-154 153.6V486h-51v282h410V486h-51zm-256-76.4c0-56.6 45.4-102.4 102-102.4S614 353 614 409.6V486H410v-76.4z">
                    </path>
                  </svg>
                `;

                zoneEl.appendChild(lock);
              }
            }

            const lab = zoneEl.querySelector('[data-role="zone-label"]');
            if (lab) lab.textContent = zLabel;

            const zp = zoneEl.querySelector('[data-role="zone-price"]');
            if (zp) {
              const p = Number(g?.product?.price || 0);
              const formattedPrice = this._formatPrice(p);
              zp.textContent = p > 0 ? `+${formattedPrice}` : formattedPrice;
            }

            zonesHost.appendChild(zoneEl);
          });
        }

        this._setupOnceContent(groupEl, g);

        if (idx >= this.maxShowGroups && !this._groupsExpanded) {
          groupEl.classList.add('cpm__group--overflow');
        }

        host.appendChild(groupEl);
      });

      if (shouldShowExpander) {
        const expanderBottom = this._groupsExpanded
          ? this._createGroupsCollapser()
          : this._createGroupsExpander(hiddenGroups);
        if (expanderBottom) host.appendChild(expanderBottom);
      }

      this._updateGroupsOpenState();
      this._updatePriceSummary();
      this._updateUIState();
    }

    _createGroupsExpander(hiddenGroups) {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__groups-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--groups';
      button.dataset.action = 'expand-groups';

      const hiddenLabels = hiddenGroups.map(g => g.label || g.group).filter(Boolean);
      const labelText = hiddenLabels.length > 0 ? ` (${hiddenLabels.join(', ')})` : ` (${hiddenGroups.length})`;
      button.innerHTML = `${this.i18n.showMore}${labelText}`;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _createGroupsCollapser() {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__groups-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--groups';
      button.dataset.action = 'collapse-groups';
      button.innerHTML = this.i18n.showLess;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // _setupOnceContent — handles text | number | file | boolean for once-groups
    // ─────────────────────────────────────────────────────────────────────────
    _setupOnceContent(groupEl, groupRec) {
      const once = !!groupRec?.once;
      const zoneOnly = !!groupRec?.zone_only;

      const type = String(groupRec?.type || 'text').toLowerCase();
      const fileMode = String(groupRec?.file_mode || 'upload').toLowerCase();
      const defaultValue = String(groupRec?.default_value || '').trim();
      const label = String(groupRec?.label || groupRec?.group || '');

      const content = groupEl.querySelector('[data-role="group-content"]');
      if (!content) return;

      const textWrap = content.querySelector('[data-role="once-text"]');
      const fileWrap = content.querySelector('[data-role="once-file"]');
      const refWrap  = content.querySelector('[data-role="once-reference"]');
      const boolWrap = content.querySelector('[data-role="once-boolean"]'); // NEW

      const hideAll = () => {
        if (textWrap) textWrap.style.display = 'none';
        if (fileWrap) fileWrap.style.display = 'none';
        if (refWrap)  refWrap.style.display  = 'none';
        if (boolWrap) boolWrap.style.display = 'none';
      };

      hideAll();

      if (zoneOnly) { content.remove(); return; }
      if (!once) return;

      // ── NEW: boolean branch ──────────────────────────────────────────────
      if (type === 'boolean') {
        if (!boolWrap) return;
        boolWrap.style.display = '';

        const boolInput = boolWrap.querySelector('[data-role="once-boolean-input"]');
        if (boolInput) {
          boolInput.dataset.sharedGroup = groupRec.group;
        }

        const boolLabel = boolWrap.querySelector('[data-role="once-boolean-label"]');
        if (boolLabel) boolLabel.textContent = label;
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      if (type === 'file') {
        if (fileMode === 'reference' && defaultValue) {
          if (refWrap) refWrap.style.display = '';

          const badge = refWrap?.querySelector('.cpm__shared-ref-badge span');
          if (badge) badge.textContent = this.i18n.sharedRef;

          groupEl.dataset.fileMode = 'reference';
          groupEl.dataset.defaultValue = defaultValue;
        } else {
          if (fileWrap) fileWrap.style.display = '';

          const input = fileWrap?.querySelector('[data-role="once-file-input"]');
          if (input) input.dataset.sharedGroup = groupRec.group;
        }
      } else {
        // text / number
        if (textWrap) textWrap.style.display = '';

        const input = textWrap?.querySelector('[data-role="once-input"]');
        if (input) {
          input.dataset.sharedGroup = groupRec.group;
          input.placeholder = label;

          if (type === 'number') {
            input.type = 'number';
            input.inputMode = 'numeric';
          } else {
            input.type = 'text';
            input.inputMode = 'text';
          }

          // Pre-fill with defaultValue if present and input is currently empty
          if (defaultValue && !input.value) input.value = defaultValue;
        }
      }
    }

    _updateGroupsOpenState() {
      const groups = Array.from(this.querySelectorAll('.cpm__group[data-once="true"], .cpm__group[data-once="false"]'))
        .filter((el) => el.dataset.once === 'true');

      groups.forEach((groupEl) => {
        const checked =
          groupEl.querySelectorAll('.cpm__zones input.cpm__cb:checked').length > 0;
        groupEl.dataset.open = checked ? 'true' : 'false';

        const contentEl = groupEl.querySelector('[data-role="group-content"]');
        if (contentEl) {
          contentEl.style.display = checked ? 'block' : 'none';
        }
      });
    }

    inspectPayload() {
      const rows = Array.isArray(this._rows) ? this._rows : [];
      const selections = this._collectSelections();
      const rowValues = this._collectRowValues();

      const selectedGroups = new Set(selections.map((s) => s.group));
      const needAnyInput = [...selectedGroups].some((g) => !this._serviceForGroup(g)?.zone_only);

      const aggFromSelections = this._aggregateServiceItemsFromSelections(selections);

      const payload = {
        baseHandle: this.baseHandle,
        rowsCount: rows.length,
        bulkItems: this._bulkItems,
        selections,
        selectedGroups: [...selectedGroups],
        needAnyInput,
        rowValues,
        aggFromSelections,
        tableData: this._buildVariantTableJSON_PrintEditor(rows, rowValues),
      };

      console.groupCollapsed('%c[club-print] INSPECT PAYLOAD', 'font-weight:700;color:#1976d2;');
      console.log(payload);
      console.groupEnd();

      return payload;
    }

    _bindUI() {
      this.addEventListener('change', (e) => {
        const el = e.target;
        if (!(el instanceof HTMLInputElement)) return;

        if (el.type === 'checkbox' && el.classList.contains('cpm__cb')) {
          const groupEl = el.closest('.cpm__group');

          if (
            groupEl?.dataset.zoneOnly === 'true' &&
            groupEl?.dataset.zoneChecked === 'true'
          ) {
            el.checked = true;
            return;
          }

          this._updateGroupsOpenState();
          this.syncFromCart(false);
          this._updatePriceSummary();

          if (this._hintState === 'validation') {
            this._clearHint();
          }

          this._scheduleUIUpdate();
          this._updateMissingFieldsHint();
          return;
        }

        // ── NEW: once-boolean toggle ─────────────────────────────────────────
        if (el.type === 'checkbox' && el.dataset.role === 'once-boolean-input') {
          if (this._hintState === 'validation') this._clearHint();
          this._scheduleUIUpdate();
          return;
        }

        // ── NEW: per-row boolean toggle ──────────────────────────────────────
        if (el.type === 'checkbox' && el.classList.contains('cpm__row-field-boolean')) {
          if (this._hintState === 'validation') this._clearHint();
          this._scheduleUIUpdate();
          return;
        }
        // ────────────────────────────────────────────────────────────────────

        if (el.type === 'file') {
          const file = el.files?.[0];

          if (!file) {
            this._clearFileInputData(el);
            this._updateFileUI(el, null);
            // E5: remove from fileStore when file is cleared
            const fileKey = el.dataset.sharedGroup
              ? `once__${el.dataset.sharedGroup}`
              : `${el.dataset.rowKey || 'once'}__${el.dataset.group || ''}__${el.dataset.zone || ''}`;
            this._fileStore?.delete(fileKey);
            this._updateMissingFieldsHint();
            return;
          }

          if (!this._validateFile(file, el)) return;

          this._updateFileUI(el, file);
          el.dataset.fileName = file.name;

          const url = this._createObjectUrl(file);
          if (url) el.dataset.previewUrl = url;

          // E5: persist file in component-level store so it survives row rebuilds
          const fileKey = el.dataset.sharedGroup
            ? `once__${el.dataset.sharedGroup}`
            : `${el.dataset.rowKey || 'once'}__${el.dataset.group || ''}__${el.dataset.zone || ''}`;
          this._fileStore.set(fileKey, file);
        }

        if (this._hintState === 'validation') {
          this._clearHint();
        }

        this._scheduleUIUpdate();
        this._updateMissingFieldsHint();
      });

      this.addEventListener('input', (e) => {
        const el = e.target;
        if (el.matches('.cpm__row-field-input, [data-role="once-input"]')) {
          if (this._hintState === 'validation') {
            this._clearHint();
          }
          this._scheduleUIUpdate();
          this._updateMissingFieldsHint();
        }
      });

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (btn) {
          const action = String(btn.dataset.action || '');

          switch (action) {
            case 'reset':
              e.preventDefault();
              this.reset();
              break;

            case 'apply':
              e.preventDefault();
              if (this._busy) return;
              this.applyToCart({ dryRun: this.dryRun || !!e.shiftKey });
              break;

            case 'remove-file':
              e.preventDefault();
              e.stopPropagation();
              this._removeOnceFile(btn);
              break;

            case 'remove-row-file':
              e.preventDefault();
              e.stopPropagation();
              this._removeRowFile(btn);
              break;

            case 'focus-field': {
              e.preventDefault();
              const fg = btn.dataset.group;
              const fz = btn.dataset.zone;
              const groupEl = this.querySelector(`.cpm__group[data-group="${fg}"]`);
              if (groupEl?.classList.contains('cpm__group--overflow')) this._expandGroups();
              const onceInput = groupEl?.querySelector('[data-role="once-input"]')
                || groupEl?.querySelector('[data-role="once-file-input"]');
              const perRowInputs = Array.from(
                this.querySelectorAll(`.cpm__row-field-input[data-group="${fg}"][data-zone="${fz}"]`)
              );
              const firstEmptyRow = perRowInputs.find(inp => !inp.value.trim());
              const input = onceInput || firstEmptyRow || perRowInputs[0];
              if (input) {
                input.scrollIntoView({ block: 'center', behavior: 'smooth' });
                input.focus();
              }
              break;
            }

            case 'delete-row': {
              e.preventDefault();
              const rk = btn.dataset.rowKey;
              const rowEl = btn.closest('.cpm__row');
              const variantId = rowEl?.dataset.variantId;

              // Hide immediately for visual feedback and renumber
              if (rowEl) rowEl.style.display = 'none';
              this._renumberVisibleRows();
              this._updatePriceSummary();
              this._updateUIState();
              this._updateMissingFieldsHint();

              if (variantId) {
                const bulkInput = document.querySelector(`.bulk-variant[data-variant-id="${variantId}"] input[type="number"]`);
                if (bulkInput) {
                  bulkInput.value = Math.max(0, (Number(bulkInput.value) || 0) - 1);
                  bulkInput.dispatchEvent(new Event('input', { bubbles: true }));
                  // The bulk rebuild re-creates rows with correct count — do NOT add rk
                  // to _deletedRowKeys here. Row keys are re-indexed after each rebuild,
                  // so the same key would collide with a surviving row of the same variant.
                  break;
                }
              }
              // Fallback: no bulk input found → no automatic rebuild → keep key hidden.
              this._deletedRowKeys.add(rk);
              break;
            }

            case 'delete-field': {
              e.preventDefault();
              const fg = btn.dataset.group;
              const fz = btn.dataset.zone;
              const fieldKey = `${btn.dataset.rowKey}__${fg}__${fz}`;
              this._deletedFields.add(fieldKey);
              const wrapEl = btn.closest('.cpm__row-field-wrap');
              if (wrapEl) wrapEl.style.display = 'none';

              // Auto-uncheck zone if all rows now have this field deleted
              const _allRows = this._bulkItems.length > 0
                ? this._getCachedRows()
                : (Array.isArray(this._rows) ? this._rows : []);
              const _activeRows = _allRows.filter(r => !this._deletedRowKeys.has(r.key));
              const allFieldsDeleted = _activeRows.length > 0 && _activeRows.every(
                r => this._deletedFields.has(`${r.key}__${fg}__${fz}`)
              );
              if (allFieldsDeleted) {
                const cb = this.querySelector(`.cpm__zones input.cpm__cb[data-group="${fg}"][data-zone="${fz}"]`);
                if (cb) {
                  cb.checked = false;
                  // Clean up stale entries so re-checking the zone works cleanly
                  _activeRows.forEach(r => this._deletedFields.delete(`${r.key}__${fg}__${fz}`));
                  this._updateGroupsOpenState();
                  this._renderRowsWithExpansion(_allRows);
                }
              }

              this._updatePriceSummary();
              this._updateMissingFieldsHint();
              break;
            }

            case 'expand-groups':
              e.preventDefault();
              this._expandGroups();
              break;

            case 'collapse-groups':
              e.preventDefault();
              this._collapseGroups();
              break;

            case 'expand-rows':
              e.preventDefault();
              this._expandRows();
              break;

            case 'collapse-rows':
              e.preventDefault();
              this._collapseRows();
              break;
          }
          this._updateUIState();
          return;
        }
      });
    }

    _expandGroups() {
      this._groupsExpanded = true;
      const host = this._el('services');
      if (!host) return;
      host.querySelectorAll('.cpm__group--overflow').forEach(el => el.classList.remove('cpm__group--overflow'));
      host.querySelector('.cpm__groups-expander')?.replaceWith(this._createGroupsCollapser());
    }

    _collapseGroups() {
      this._groupsExpanded = false;
      const host = this._el('services');
      if (!host) return;
      const groups = Array.from(host.querySelectorAll('.cpm__group[data-group]'));
      groups.forEach((el, idx) => {
        el.classList.toggle('cpm__group--overflow', idx >= this.maxShowGroups);
      });
      const hiddenGroups = this.data.slice(this.maxShowGroups);
      const newExpander = this._createGroupsExpander(hiddenGroups);
      host.querySelector('.cpm__groups-expander')?.replaceWith(newExpander);
      newExpander.scrollIntoView({ block: 'start' });
    }

    _expandRows() {
      this._rowsExpanded = true;
      const rows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);
      this._renderRowsWithExpansion(rows);
    }

    _collapseRows() {
      this._rowsExpanded = false;

      const rows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : this._rows;

      this._renderRowsWithExpansion(rows);

      this.querySelector('.cpm__rows')
        ?.scrollIntoView({ block: 'start' });
    }

    reset() {
      this._savedGroupState.clear();
      this._deletedRowKeys.clear();
      this._deletedFields.clear();
      this.querySelectorAll('input.cpm__cb').forEach(cb => {
        const groupEl = cb.closest('.cpm__group');

        if (
          groupEl?.dataset.zoneOnly === 'true' &&
          groupEl?.dataset.zoneChecked === 'true'
        ) {
          cb.checked = true;
        } else {
          cb.checked = false;
        }
      });

      this.querySelectorAll('.cpm__row-field-input').forEach((inp) => (inp.value = ''));
      this.querySelectorAll('[data-role="once-input"]').forEach((inp) => (inp.value = ''));
      // ── NEW: reset boolean inputs ─────────────────────────────────────────
      this.querySelectorAll('[data-role="once-boolean-input"]').forEach((inp) => (inp.checked = false));
      this.querySelectorAll('.cpm__row-field-boolean').forEach((inp) => (inp.checked = false));
      // ─────────────────────────────────────────────────────────────────────
      this.querySelectorAll('input[type="file"]').forEach((inp) => this._resetFileInput(inp));

      this._updateGroupsOpenState();
      this._clearHint();
      this.syncFromCart(false);
      this._updatePriceSummary();
      this._updateUIState();
    }

    _validateFile(file, input) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(this.i18n.fileTooBig);
        this._resetFileInput(input);
        return false;
      }

      const allowed = ['image/svg+xml', 'image/png', 'image/jpeg'];
      if (!allowed.includes(file.type)) {
        alert(this.i18n.fileInvalidType);
        this._resetFileInput(input);
        return false;
      }

      return true;
    }

    _revokeAllObjectUrls() {
      this._objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
      this._objectUrls.clear();
      if (this._fileStore) this._fileStore.clear();
    }

    _createObjectUrl(file) {
      const url = fileToObjectURL(file);
      if (url) this._objectUrls.add(url);
      return url;
    }

    _revokeObjectUrl(url) {
      if (!url || !this._objectUrls.has(url)) return;
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
      this._objectUrls.delete(url);
    }

    _clearFileInputData(input) {
      if (input.dataset.previewUrl) this._revokeObjectUrl(input.dataset.previewUrl);
      delete input.dataset.fileName;
      delete input.dataset.previewUrl;
    }

    _resetFileInput(input) {
      input.value = '';
      this._clearFileInputData(input);
      this._updateFileUI(input, null);
      // E5: remove from component-level file store
      const fileKey = input.dataset.sharedGroup
        ? `once__${input.dataset.sharedGroup}`
        : `${input.dataset.rowKey || 'once'}__${input.dataset.group || ''}__${input.dataset.zone || ''}`;
      this._fileStore?.delete(fileKey);
    }

    _updateFileUI(input, file) {
      const onceFile = input.closest('[data-role="once-file"]');
      if (onceFile) {
        const groupEl = input.closest('.cpm__group');
        if (!groupEl) return;

        const preview = groupEl.querySelector('[data-role="file-preview"]');
        const nameEl = groupEl.querySelector('[data-role="file-name"]');

        if (file) {
          groupEl.dataset.hasFile = 'true';
          if (nameEl) nameEl.textContent = file.name;
          if (preview) preview.style.display = '';
        } else {
          groupEl.dataset.hasFile = 'false';
          if (nameEl) nameEl.textContent = '';
          if (preview) preview.style.display = 'none';
        }
        return;
      }

      const rowWrap = input.closest('.cpm__row-field--file');
      if (!rowWrap) return;

      const nameEl = rowWrap.querySelector('[data-role="file-name"]');
      rowWrap.dataset.hasFile = file ? 'true' : 'false';
      if (nameEl) nameEl.textContent = file ? file.name : '';
    }

    _removeOnceFile(btn) {
      const groupEl = btn.closest('.cpm__group');
      if (!groupEl) return;

      const input = groupEl.querySelector('[data-role="once-file-input"]');
      if (input) this._resetFileInput(input);
    }

    _removeRowFile(btn) {
      const wrap = btn.closest('.cpm__row-field--file');
      if (!wrap) return;

      const input = wrap.querySelector('input[type="file"]');
      if (input) this._resetFileInput(input);
    }

    async syncFromCart(initial = false) {
      if (this._bulkItems.length > 0) {
        this._updateRowsFromBulk();
        return;
      }

      if (this._busy) return;

      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      this._busy = true;
      this._setStatus(initial ? this.i18n.loading : '');

      try {
        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this._abortController.signal,
        }).then((r) => {
          if (!r.ok) throw new Error(`Cart fetch failed: ${r.status}`);
          return r.json();
        });

        this._renderRowsFromCart(cart);
        this._updatePriceSummary();
        this._setStatus('');
      } catch (e) {
        if (e.name === 'AbortError') return;
        this._warn('cart sync failed', e);
        this._setStatus(this.i18n.errorCart);
      } finally {
        this._busy = false;
        this._abortController = null;
      }
    }

_renderRowsFromCart(cartState) {

  const savedState = this._saveRowState();

  const rowsHost = this._el('rowsHost');
  const metaHost = this._el('rowsMeta');
  if (!rowsHost) return;

  const rows = this._buildRowsFromCart(cartState);
  this._rows = rows;

  const servicesEl = this._el('services') || this.querySelector('[data-role="services"]');
  if (servicesEl) {
    servicesEl.style.display = '';
  }

  if (metaHost) {
    metaHost.textContent = rows.length
      ? `${rows.length} ${this.i18n.rowsSuffix}`
      : this.i18n.rowsEmpty;
  }

  this._renderRowsWithExpansion(rows);

  this._restoreRowState(savedState);

  this._updateUIState();
}

    // ─────────────────────────────────────────────────────────────────────────
    // _fillRowInputs — renders per-row inputs incl. boolean
    // ─────────────────────────────────────────────────────────────────────────
    _fillRowInputs(host, rowKey, selections) {
      const fragment = document.createDocumentFragment();
      if (!selections) selections = this._collectSelections();

      if (!selections.length) {
        const empty = this._clone('rowEmpty');
        if (empty) fragment.appendChild(empty);
        host.innerHTML = '';
        host.appendChild(fragment);
        return;
      }

      const perRowSelections = selections.filter((s) => {
        const rec = this._serviceForGroup(s.group);
        if (!rec) return false;
        return !rec.once && !rec.zone_only;
      });

      perRowSelections.forEach((s) => {
        const groupRec = this._serviceForGroup(s.group);
        const type = String(groupRec?.type || 'text').toLowerCase();
        const label = String(groupRec?.label || s.group || '');
        const fileMode = String(groupRec?.file_mode || 'upload').toLowerCase();
        const defaultValue = String(groupRec?.default_url || '').trim();

        let rowInp = null;

        if (type === 'file') {
          if (fileMode === 'reference' && defaultValue) return;

          rowInp = this._clone('rowFileInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone;

          const field = rowInp.querySelector('input[type="file"]');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
          }

        } else if (type === 'boolean') {
          // ── NEW: per-row boolean ─────────────────────────────────────────
          rowInp = this._clone('rowBooleanInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone || label;

          const field = rowInp.querySelector('.cpm__row-field-boolean');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
          }
          // ────────────────────────────────────────────────────────────────

        } else {
          rowInp = this._clone('rowInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone;

          const field = rowInp.querySelector('.cpm__row-field-input');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
            field.placeholder = label;

            if (type === 'number') {
              field.type = 'number';
              field.inputMode = 'numeric';
            }
          }
        }

        if (rowInp) {
          const fieldKey = `${rowKey}__${s.group}__${s.zone}`;

          const delBtn = document.createElement('button');
          delBtn.type = 'button';
          delBtn.className = 'cpm__field-delete-btn';
          delBtn.dataset.action = 'delete-field';
          delBtn.dataset.rowKey = rowKey;
          delBtn.dataset.group = s.group;
          delBtn.dataset.zone = s.zone;
          delBtn.title = 'Feld entfernen';
          delBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

          const wrap = document.createElement('div');
          wrap.className = 'cpm__row-field-wrap';
          wrap.appendChild(rowInp);
          wrap.appendChild(delBtn);

          if (this._deletedFields.has(fieldKey)) wrap.style.display = 'none';
          fragment.appendChild(wrap);
        }
      });

      host.innerHTML = '';
      host.appendChild(fragment);
    }

    _formatRowTitleFromLineItem(li) {
      const vals = (() => {
        if (Array.isArray(li?.options_with_values) && li.options_with_values.length) {
          return li.options_with_values
            .map((o) => String(o?.value || '').trim())
            .filter(Boolean)
            .filter((v) => v !== 'Default Title');
        }
        if (Array.isArray(li?.variant_options) && li.variant_options.length) {
          return li.variant_options
            .map((v) => String(v || '').trim())
            .filter(Boolean)
            .filter((v) => v !== 'Default Title');
        }
        return [];
      })();

      // E3: single esc definition used by both branches
      const esc = (s) =>
        String(s).replace(/[&<>"']/g, (m) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[m]));

      if (!vals.length) {
        const productTitle = String(li?.product_title || li?.product?.title || '').trim();
        if (productTitle) {
          return `<span class="cpm__row-title-part cpm__row-title-part--first">${esc(productTitle)}</span>`;
        }

        return '';
      }

      let ordered = vals;
      if (vals.length >= 2) ordered = [vals[vals.length - 1], ...vals.slice(0, -1)];

      return ordered
        .map((v, idx) => {
          const cls =
            idx === 0
              ? 'cpm__row-title-part cpm__row-title-part--first'
              : 'cpm__row-title-part';
          return `<span class="${cls}">${esc(v)}</span>`;
        })
        .join(`<span class="cpm__row-title-sep">-</span>`);
    }

    _buildRowsFromCart(cart) {
      const rows = [];
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const baseItems = items.filter(li =>
        li.handle === this.baseHandle &&
        !li.properties?._MpeBatch
      );

      let idx = 0;
      baseItems.forEach((li) => {
        const vId = String(li.variant_id || '');
        const qty = Number(li.quantity || 0);
        const title = this._formatRowTitleFromLineItem(li) || '';

        for (let i = 1; i <= qty; i++) {
          idx += 1;
          rows.push({ key: `${vId}#${idx}`, variant_id: vId, title });
        }
      });

      return rows;
    }

    _collectSelections() {
      const out = [];
      this.querySelectorAll('.cpm__zones input.cpm__cb:checked').forEach((cb) => {
        const group    = String(cb.dataset.group    || '').trim();
        const zone     = String(cb.dataset.zone     || '').trim();
        const zone_i18 = String(cb.dataset.zoneI18  || '').trim();
        if (!group || !zone) return;
        out.push({ group, zone, zone_i18 });
      });
      return out;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // _collectRowValues — collects text | file | boolean values
    // ─────────────────────────────────────────────────────────────────────────
    _collectRowValues() {
      const out = [];
      const rows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);
      if (!rows.length) return out;

      // text / number per-row
      this.querySelectorAll('.cpm__row-field-input').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        const v = String(f.value || '').trim();
        if (!row_key || !g || !z || !v) return;
        if (this._deletedRowKeys.has(row_key)) return;
        if (this._deletedFields.has(`${row_key}__${g}__${z}`)) return;
        out.push({ row_key, g, z, i18, v, fileObj: null, fileMode: 'text' });
      });

      this.querySelectorAll('.cpm__row-field-boolean').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        if (!row_key || !g || !z) return;
        if (!f.checked) return;
        if (this._deletedRowKeys.has(row_key)) return;
        if (this._deletedFields.has(`${row_key}__${g}__${z}`)) return;
        out.push({ row_key, g, z, i18, v: 'true', fileObj: null, fileMode: 'boolean' });
      });

      // file per-row
      this.querySelectorAll('.cpm__row-field--file input[type="file"]').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        // E5: fall back to fileStore when FileList is lost after a row rebuild
        const file = f.files?.[0] || (() => {
          const fk = `${f.dataset.rowKey || 'once'}__${f.dataset.group || ''}__${f.dataset.zone || ''}`;
          return this._fileStore?.get(fk) || null;
        })();
        if (!row_key || !g || !z || !file) return;
        if (this._deletedRowKeys.has(row_key)) return;
        if (this._deletedFields.has(`${row_key}__${g}__${z}`)) return;
        out.push({
          row_key,
          g,
          z,
          i18,
          v: file.name,
          fileObj: file,
          fileMode: 'upload',
        });
      });

      // zone-only groups: fixed zones with a pre-set default value (no user input needed)
      const zoneOnlyGroups = Array.from(this.querySelectorAll('.cpm__group[data-zone-only="true"]'));
      zoneOnlyGroups.forEach((groupEl) => {
        const g = String(groupEl.dataset.group || '').trim();
        const value = String(groupEl.dataset.defaultValue || '').trim();
        if (!g || !value) return;
        const zoneCbs = Array.from(groupEl.querySelectorAll('.cpm__zones input.cpm__cb:checked'));
        rows.forEach((r) => {
          zoneCbs.forEach((cb) => {
            const z = String(cb.dataset.zone || '').trim();
            const i18 = String(cb.dataset.zoneI18 || '').trim();
            if (!z) return;
            out.push({ row_key: r.key, g, z, i18, v: value, fileObj: null, fileMode: 'reference' });
          });
        });
      });

      // once-groups
      const onceGroups = Array.from(this.querySelectorAll('.cpm__group[data-once="true"], .cpm__group'))
        .filter((el) => el.dataset.once === 'true');

      onceGroups.forEach((groupEl) => {
        const zoneCbs = Array.from(groupEl.querySelectorAll('.cpm__zones input.cpm__cb:checked'));
        if (!zoneCbs.length) return;

        const g = String(groupEl.dataset.group || '').trim();
        const type = String(groupEl.dataset.type || 'text').toLowerCase();
        if (!g) return;

        let value = '';
        let fileObj = null;
        let fileMode = 'once';

        if (type === 'boolean') {
          value = 'true';
          fileMode = 'once-boolean';
        } else if (type === 'file') {
          const fm = String(groupEl.dataset.fileMode || 'upload').toLowerCase();

          if (fm === 'reference') {
            value = String(groupEl.dataset.defaultValue || '').trim();
            if (!value) return;
            fileMode = 'reference';
          } else {
            const inp = groupEl.querySelector('[data-role="once-file-input"]');
            const file = inp?.files?.[0];
            if (!file) return;
            value = file.name;
            fileObj = file;
            fileMode = 'once';
          }
        } else {
          const inp = groupEl.querySelector('[data-role="once-input"]');
          value = String(inp?.value || '').trim();
          if (!value) return;
          // If the value matches the group's preset defaultValue, treat as reference
          // so the cart property shows "Type - Zone" without "(value)" / Inhalt.
          const dv = String(groupEl.dataset.defaultValue || '').trim();
          fileMode = (dv && value === dv) ? 'reference' : 'once-text';
        }

        zoneCbs.forEach((cb) => {
          const z = String(cb.dataset.zone || '').trim();
          const i18 = String(cb.dataset.zoneI18 || '').trim();
          if (!z) return;

          rows.forEach((r) => {
            out.push({
              row_key: r.key,
              g,
              z,
              i18,
              v: value,
              fileObj,
              fileMode,
            });
          });
        });
      });

      return out;
    }

    _serviceForGroup(group) {
      const g = String(group || '').trim();
      return this.data.find((x) => String(x.group || '').trim() === g) || null;
    }

    _serviceVariantIdForGroup(group) {
      const rec = this._serviceForGroup(group);
      const vid = rec?.product?.variant_id;
      const n = Number(vid);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    _serviceHandleForGroup(group) {
      const rec = this._serviceForGroup(group);
      return String(rec?.product?.handle || '').trim();
    }

    _buildVariantTableJSON_PrintEditor(rows, rowValues) {
      const byRow = new Map();
      rowValues.forEach((x) => {
        if (!byRow.has(x.row_key)) byRow.set(x.row_key, []);
        byRow.get(x.row_key).push(x);
      });

      const rs = rows.map((r) => {
        const inptsRaw = byRow.get(r.key) || [];
        const inpts = inptsRaw.map((x) => ({
          g: x.g,
          h: this._serviceHandleForGroup(x.g) || '',
          v: x.v,
        }));
        return {
          key: r.key,
          v_id: String(r.variant_id || ''),
          base_h: '',
          inpts,
        };
      });

      const out = {
        hdl: this.baseHandle,
        hdls: [this.baseHandle],
        rs,
      };

      if (this.clubCollection?.handle) {
        out.club_collection = {
          handle: this.clubCollection.handle,
          title: this.clubCollection.title || ''
        };
      }

      return out;
    }
    _formatPrice(cents) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    }

    _calculateServiceTotal() {
      const selections = this._collectSelections();
      const allRows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);
      const activeRows = allRows.filter(r => !this._deletedRowKeys.has(r.key));
      if (!selections.length || !activeRows.length) return 0;

      let total = 0;
      selections.forEach((s) => {
        const rec = this._serviceForGroup(s.group);
        if (!rec) return;
        const price = Number(rec?.product?.price || 0);
        const rowsForZone = (rec.once || rec.zone_only)
          ? activeRows.length
          : activeRows.filter(r => !this._deletedFields.has(`${r.key}__${s.group}__${s.zone}`)).length;
        total += price * rowsForZone;
      });

      return total;
    }

    _updatePriceSummary() {
      const summaryEl = this.querySelector('[data-role="price-summary"]');
      if (!summaryEl) return;

      const serviceTotal = this._calculateServiceTotal();
      const articleTotal = this._calculateArticleTotal();
      const grandTotal = serviceTotal + articleTotal;

      const serviceEl = summaryEl.querySelector('[data-role="service-total"]');
      const articleEl = summaryEl.querySelector('[data-role="article-total"]');
      const grandEl = summaryEl.querySelector('[data-role="grand-total"]');

      if (serviceEl) serviceEl.textContent = this._formatPrice(serviceTotal);
      if (articleEl) articleEl.textContent = this._formatPrice(articleTotal);
      if (grandEl) grandEl.textContent = this._formatPrice(grandTotal);

      summaryEl.style.display = grandTotal > 0 ? '' : 'none';
    }

    _genBatchId() {
      return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    }

    _openCartDrawerFallback() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.assign('/cart');
    }

    _sectionsToRender() {
      return [
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#shopify-section-cart-icon-bubble' },
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];
    }

    async _fetchSectionsHTML(sections, sectionsUrl) {
      const url = new URL(sectionsUrl || window.location.href);
      url.searchParams.set('sections', sections.join(','));

      const resp = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const result = {};

      sections.forEach((name) => {
        const el = doc.querySelector(`#shopify-section-${name}`);
        if (el) result[name] = el.outerHTML;
      });

      return { sections: result, items: [] };
    }

    _renderSectionsToDOM(parsedState) {
      if (!parsedState || !parsedState.sections) return;

      this._sectionsToRender().forEach(({ id, selector, section }) => {
        const sectionHTML = parsedState.sections[section];
        if (!sectionHTML || typeof sectionHTML !== 'string') return;

        let container = document.getElementById(id);
        if (!container && section === 'cart-drawer') {
          container =
            document.querySelector('#CartDrawer .drawer__inner')?.closest('cart-drawer') ||
            null;
        }

        const dom = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const frag = dom.querySelector(selector);
        if (!frag) return;

        if (section === 'cart-drawer') {
          const target = document.querySelector('#CartDrawer .drawer__inner');
          if (target) target.innerHTML = frag.innerHTML;

          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.toggle('is-empty', (parsedState.items || []).length === 0);
        } else if (container) {
          const target = container.querySelector(selector) || container;
          target.innerHTML = frag.innerHTML;
        }
      });
    }

    async _refreshCartSections() {
      const sections = this._sectionsToRender().map((s) => s.section);
      const sections_url = window.location.pathname + window.location.search;
      const updateUrl = window.routes?.cart_update_url || '/cart/update.js';

      try {
        const resp = await fetch(updateUrl, {
          ...jsonFetchConfig('POST'),
          body: JSON.stringify({ updates: {}, sections, sections_url }),
        });

        const parsed = await resp.json();
        this._renderSectionsToDOM(parsed);
        return parsed;
      } catch (e) {
        this._warn('refresh cart sections failed, using fallback', e);
        const fallback = await this._fetchSectionsHTML(sections, sections_url);
        this._renderSectionsToDOM(fallback);
        return fallback;
      }
    }

    _publishCartEvents(parsedState) {
      if (!hasPub()) return;
      try {
        window.publish(EVENTS().cartUpdate, { source: 'club-print-mini-editor', cartData: parsedState });
        window.publish(EVENTS().quantityUpdate, { source: 'club-print-mini-editor' });
      } finally {
        window.publish(EVENTS().cartRequestEnd);
      }
    }

    _dryRunLog(label, payload) {
      console.groupCollapsed(`%c[club-print] DRY-RUN: ${label}`, 'font-weight:600;color:#1976d2;');
      try {
        console.log(payload);
      } finally {
        console.groupEnd();
      }
    }

    async applyToCart({ dryRun = false } = {}) {
      if (this._busy) return;

      const rows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);

      if (!rows.length) {
        this._setHint(this._bulkItems.length > 0 ? 'Keine Artikel ausgewählt' : this.i18n.hintNoRows, 'warning');
        return;
      }

      const selections = this._collectSelections();
      const hasBulk = this._bulkItems.length > 0;

      if (!selections.length && !hasBulk) {
        this._setHint(this.i18n.hintNoSelections, 'warning');
        return;
      }

      const rowValues = this._collectRowValues();
      const selectedGroups = new Set(selections.map((s) => s.group));
      const needAnyInput = [...selectedGroups].some((g) => {
        const rec = this._serviceForGroup(g);
        return rec && !rec.zone_only;
      });

      this._busy = true;
      this.classList.add('is-busy');
      this._setHint(this.i18n.hintSaving, 'saving');

      try {
        this._batchId = this._genBatchId();

        if (hasPub()) window.publish(EVENTS().cartRequestStart);

        // ── Build indexed "Druck N" print list ────────────────────────────────
        // once-prints: batch-level (same for all jerseys), deduplicated by group+zone
        const _oncePrints = [];
        const _onceKeys = new Set();
        rowValues.forEach((rv) => {
          if (!['once', 'reference', 'once-text', 'once-boolean'].includes(rv.fileMode)) return;
          const key = `${rv.g}|${rv.z}`;
          if (_onceKeys.has(key)) return;
          _onceKeys.add(key);
          _oncePrints.push(rv);
        });

        // Readable label for a group id
        const _druckTyp = (g) => this._serviceForGroup(g)?.label || g;

        // Console helper
        const _logItem = (label, attrs) => {
          console.group(`%c[add-to-cart] ${label}`, 'color:#1565c0;font-weight:700');
          Object.entries(attrs).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') return;
            const display = typeof v === 'string' && v.length > 120 ? v.slice(0, 120) + '…' : v;
            console.log(`  %c${k}%c = %o`, 'color:#6a1b9a;font-weight:600', '', display);
          });
          console.groupEnd();
        };
        // ─────────────────────────────────────────────────────────────────────

        // Compute filledSelections before physical loop so we know whether to attach _MpeBatch
        const filledKeys = new Set(rowValues.map(v => `${v.g}::${v.z}`));
        const zoneOnlySelections = [];
        for (const rec of this.data) {
          if (!rec.zone_only) continue;
          const vid = this._serviceVariantIdForGroup(rec.group);
          if (!vid) continue;
          for (const z of (Array.isArray(rec.zones) ? rec.zones : [])) {
            const zKey = String(z?.key || '').trim();
            if (zKey) zoneOnlySelections.push({ group: rec.group, zone: zKey, zone_i18: String(z?.i18 || '').trim() });
          }
        }
        const filledSelections = [
          ...zoneOnlySelections,
          ...selections.filter(s => {
            const rec = this._serviceForGroup(s.group);
            return rec && !rec.zone_only && filledKeys.has(`${s.group}::${s.zone}`);
          }),
        ];
        const hasServices = this.addServices && filledSelections.length > 0;

        const physicalRows = this._getCachedRows()
          .filter(r => !this._deletedRowKeys.has(r.key));

        if (this._bulkItems.length > 0) {
          // All physical items in one JSON batch — avoids FormData multi-item parsing issues
          // and eliminates rate limiting from sequential calls
          const _actualFixedCount = this.querySelectorAll(
            '.cpm__group[data-zone-only="true"][data-zone-checked="true"] .cpm__cb:checked'
          ).length;

          const physicalPayload = physicalRows.map((row, idx) => {
            const rowPrints = rowValues.filter((rv) =>
              rv.row_key === row.key && ['text', 'boolean', 'upload'].includes(rv.fileMode)
            );
            const allPrints = [..._oncePrints, ...rowPrints];

            const properties = {};
            if (hasServices) {
              properties['_MpeBatch']      = this._batchId;
              properties['_MpeFixedZones'] = _actualFixedCount > 0 ? '1' : '0';
            }

            const logPrints = {};
            allPrints.forEach((p, n) => {
              const d   = n + 1;
              const zone = p.i18 || p.z;
              const typ  = _druckTyp(p.g);
              const val  = String(p.v);
              const isBoolean = val === 'true' || val === 'false';

              const propVal = p.fileObj
                ? `${typ} - ${zone}(${p.fileObj.name})`
                : (isBoolean || p.fileMode === 'reference')
                  ? `${typ} - ${zone}`
                  : `${typ} - ${zone}(${val})`;

              properties[`#${d}`] = propVal;
              logPrints[`#${d}`]  = propVal;
            });

            const bi = row.bulk_item;
            _logItem(`Physical ${idx + 1}/${physicalRows.length}: ${bi?.product_title} ${bi?.color} ${bi?.size}`, {
              variant_id: row.variant_id,
              quantity:   1,
              ...(hasServices ? { _MpeBatch: this._batchId } : {}),
              ...logPrints,
            });

            return { id: row.variant_id, quantity: 1, properties };
          });

          if (dryRun) {
            this._dryRunLog('Physical items (dry-run)', { count: physicalRows.length, items: physicalPayload });
          } else {
            const resp = await fetch('/cart/add.js', {
              ...jsonFetchConfig('POST'),
              body: JSON.stringify({ items: physicalPayload }),
            });
            if (!resp.ok) {
              const t = await resp.text().catch(() => '');
              throw new Error(`Physical items add failed: ${resp.status} ${t}`);
            }
          }
        }

        if (hasServices) {
          let agg = this._aggregateServiceItemsFromSelections(filledSelections);

          if (agg.length) {
            agg = this._sortAggByConfigOrder(agg);

            const fileUploads = _oncePrints.filter(p => p.fileObj);
            const hasFileUploads = fileUploads.length > 0;

            // If first service item carries file uploads, send it via FormData separately
            if (hasFileUploads) {
              const b     = agg[0];
              const ratio = physicalRows.length > 0 ? b.quantity / physicalRows.length : 1;
              const fd    = new FormData();
              fd.append('items[0][id]',                          String(b.id));
              fd.append('items[0][quantity]',                    String(b.quantity));
              fd.append('items[0][properties][_MpeBatch]',       this._batchId);
              fd.append('items[0][properties][_PrintRatio]',     String(ratio));
              fileUploads.forEach((p, n) => {
                fd.append(`items[0][properties][#${n + 1} ${p.fileObj.name}]`, p.fileObj, p.fileObj.name);
              });

              _logItem(`Service item 1/${agg.length} (file upload): variant ${b.id}`, {
                variant_id: b.id, quantity: b.quantity, _MpeBatch: this._batchId, _PrintRatio: ratio,
              });

              if (!dryRun) {
                const resp = await fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', body: fd });
                if (!resp.ok) {
                  const t = await resp.text().catch(() => '');
                  throw new Error(`Service item 1 (file) add failed: ${resp.status} ${t}`);
                }
              }
            }

            // Remaining service items (or all if no file uploads) in one JSON batch
            const serviceItems = hasFileUploads ? agg.slice(1) : agg;

            if (serviceItems.length > 0) {
              const servicePayload = serviceItems.map((b, idx) => {
                const ratio = physicalRows.length > 0 ? b.quantity / physicalRows.length : 1;
                _logItem(`Service item ${idx + (hasFileUploads ? 2 : 1)}/${agg.length}: variant ${b.id}`, {
                  variant_id: b.id, quantity: b.quantity, _MpeBatch: this._batchId, _PrintRatio: ratio,
                });
                return {
                  id:         b.id,
                  quantity:   b.quantity,
                  properties: { _MpeBatch: this._batchId, _PrintRatio: String(ratio) },
                };
              });

              if (dryRun) {
                this._dryRunLog('Service items (dry-run)', { items: servicePayload });
              } else {
                const resp = await fetch('/cart/add.js', {
                  ...jsonFetchConfig('POST'),
                  body: JSON.stringify({ items: servicePayload }),
                });
                if (!resp.ok) {
                  const t = await resp.text().catch(() => '');
                  throw new Error(`Service items add failed: ${resp.status} ${t}`);
                }
              }
            }
          }
        }

        if (dryRun) {
          this._setHint(`${this.i18n.hintSaved} (dry-run)`, 'success');
          if (hasPub()) window.publish(EVENTS().cartRequestEnd);
          return;
        }

        const parsedState = await this._refreshCartSections();
        this._publishCartEvents(parsedState);

        try { localStorage.setItem('mpe:lastBatchId', this._batchId); } catch (_) {}
        this._setHint(this.i18n.hintSaved, 'success');

        this._resetBulkComponent();

        this.reset();
        this._scrollToBulkComponent();
        if (this.openDrawer) this._openCartDrawerFallback();

      } catch (e) {
        this._warn('apply failed', e);
        if (hasPub()) window.publish(EVENTS().cartRequestEnd);
        this._setHint(`${this.i18n.hintError} (${e.message})`, 'error');
      } finally {
        this._busy = false;
        this.classList.remove('is-busy');
      }
    }

    _aggregateServiceItemsFromSelections(selections) {
      const byVariant = new Map();
      const allRows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);
      const activeRows = allRows.filter(r => !this._deletedRowKeys.has(r.key));

      selections.forEach((s) => {
        const vid = this._serviceVariantIdForGroup(s.group);
        const rec = this._serviceForGroup(s.group);
        if (!vid) {
          this._warn(`_aggregateServiceItemsFromSelections: variant_id=0 for group "${s.group}" — service skipped silently`);
          return;
        }
        if (!activeRows.length) return;

        const rowsForZone = (rec?.once || rec?.zone_only)
          ? activeRows.length
          : activeRows.filter(r => !this._deletedFields.has(`${r.key}__${s.group}__${s.zone}`)).length;

        if (!rowsForZone) return;

        const k = String(vid);
        if (!byVariant.has(k)) {
          byVariant.set(k, {
            id: vid,
            quantity: 0,
            groups: new Set(),
            handle: this._serviceHandleForGroup(s.group) || '',
          });
        }

        const b = byVariant.get(k);
        b.quantity += rowsForZone;
        b.groups.add(s.group);
      });

      return Array.from(byVariant.values()).map((x) => ({
        id: x.id,
        quantity: x.quantity,
        groups: Array.from(x.groups),
        handle: x.handle || '',
      }));
    }

    _sortAggByConfigOrder(agg) {
      const idxByGroup = new Map(
        this.data.map((rec, idx) => [String(rec?.group || '').trim(), idx])
      );

      const score = (b) => {
        const gs = Array.isArray(b?.groups) ? b.groups : [];
        let max = -1;
        gs.forEach((g) => {
          const i = idxByGroup.get(String(g || '').trim());
          if (typeof i === 'number' && i > max) max = i;
        });
        return max;
      };

      return agg.slice().sort((a, b) => score(a) - score(b));
    }

    _setHint(message, type = 'info') {
      this._hintState = type;
      const el = this._el('hint');
      if (!el) return;

      el.textContent = message || '';

      el.classList.remove('hint-info', 'hint-warning', 'hint-validation', 'hint-saving', 'hint-success', 'hint-error');

      if (type) {
        el.classList.add(`hint-${type}`);
      }

      if (type === 'success') {
        setTimeout(() => {
          if (this._hintState === 'success') {
            this._clearHint();
            this._updateUIState();
          }
        }, 2000);
      }

      if (type === 'validation') {
        setTimeout(() => {
          if (this._hintState === 'validation') {
            this._clearHint();
            this._updateUIState();
          }
        }, 4000);
      }
    }

    _clearHint() {
      this._hintState = 'idle';
      this._setHint('');
    }

    _renumberVisibleRows() {
      Array.from(this.querySelectorAll('.cpm__row'))
        .filter(el => el.style.display !== 'none')
        .forEach((rowEl, i) => {
          const numEl = rowEl.querySelector('[data-role="row-number"]');
          if (numEl) numEl.textContent = `${i + 1}.`;
        });
    }

    _updateMissingFieldsHint() {
      if (['validation', 'saving', 'error', 'success', 'warning'].includes(this._hintState)) return;

      const selections = this._collectSelections();
      const rowValues = this._collectRowValues();
      const selectedGroups = new Set(selections.map(s => s.group));
      const needAnyInput = [...selectedGroups].some(g => {
        const rec = this._serviceForGroup(g);
        return rec && !rec.zone_only;
      });

      if (!needAnyInput) {
        if (this._hintState === 'info') this._clearHint();
        return;
      }

      const allRows = this._bulkItems.length > 0
        ? this._getCachedRows()
        : (Array.isArray(this._rows) ? this._rows : []);
      const rows = allRows.filter(r => !this._deletedRowKeys.has(r.key));

      if (!rows.length) {
        if (this._hintState === 'info') this._clearHint();
        return;
      }

      const totalRows = rows.length;

      const realValues = rowValues.filter(v => v.fileMode !== 'boolean' && v.fileMode !== 'once-boolean');

      const missingText = [];
      const missingFile = [];

      selections.forEach(({ group, zone, zone_i18 }) => {
        const rec = this._serviceForGroup(group);
        if (!rec || rec.zone_only) return;
        const type = String(rec.type || '').toLowerCase();
        if (type === 'boolean') return;

        const isOnce = rec.once || type === 'file';
        let missingCount;

        if (isOnce) {
          const hasPair = realValues.some(v => v.g === group && v.z === zone);
          missingCount = hasPair ? 0 : totalRows;
        } else {
          const activeRows = rows.filter(r => !this._deletedFields.has(`${r.key}__${group}__${zone}`));
          const filledCount = realValues.filter(v => v.g === group && v.z === zone).length;
          missingCount = activeRows.length - filledCount;
        }

        if (missingCount > 0) {
          const item = { group, zone, zone_i18, label: rec.label || group, missingCount };
          if (type === 'file') missingFile.push(item);
          else missingText.push(item);
        }
      });

      const buildBtns = (items) => items.map(({ group, zone, zone_i18, label, missingCount }) => {
        const display = zone_i18 ? `${label} / ${zone_i18}` : label;
        const count = totalRows > 1 ? ` (${missingCount} Stk)` : '';
        return `<button type="button" class="cpm__hint-field-btn" data-action="focus-field" data-group="${group}" data-zone="${zone}">${display}${count}</button>`;
      }).join('');

      const parts = [];
      if (missingText.length) parts.push(`Felder leer: ${buildBtns(missingText)}`);
      if (missingFile.length) parts.push(`Datei erforderlich: ${buildBtns(missingFile)}`);

      if (parts.length) {
        // E15: set innerHTML directly so <button> elements render; state/classes set manually
        this._hintState = 'info';
        const hintEl = this._el('hint');
        if (hintEl) {
          hintEl.innerHTML = `${parts.join(' · ')} — wird übersprungen`;
          hintEl.classList.remove('hint-info', 'hint-warning', 'hint-validation', 'hint-saving', 'hint-success', 'hint-error');
          hintEl.classList.add('hint-info');
        }
      } else {
        if (this._hintState === 'info') this._clearHint();
      }
    }

    _updateUIState() {
      const resetBtn = this._el('resetBtn');
      const hintEl = this._el('hint');

      const hasPhysical = this._bulkItems.length > 0 || (Array.isArray(this._rows) && this._rows.length > 0);

      const hasNonFixedZones = Array.from(this.querySelectorAll('.cpm__cb:checked')).some(cb => {
        const groupEl = cb.closest('.cpm__group');
        const isFixed = groupEl?.dataset.zoneOnly === 'true' && groupEl?.dataset.zoneChecked === 'true';
        return !isFixed;
      });

      const hasTextValues = Array.from(
        this.querySelectorAll('.cpm__row-field-input, [data-role="once-input"]')
      ).some(el => String(el.value || '').trim() !== '');

      const hasFiles = Array.from(
        this.querySelectorAll('input[type="file"]')
      ).some(el => el.files && el.files.length);

      // ── NEW: boolean "touched" only when checked (unchecked = default state)
      const hasBooleans = Array.from(
        this.querySelectorAll('[data-role="once-boolean-input"], .cpm__row-field-boolean')
      ).some(el => el.checked);

      const hasPersonalization = hasNonFixedZones || hasTextValues || hasFiles || hasBooleans;

      if (resetBtn) {
        resetBtn.style.display = hasPersonalization ? '' : 'none';
      }

      // Hide rows body when no personalization zones are selected
      const hasAnyCheckbox = this.querySelectorAll('.cpm__cb:checked').length > 0;
      const rowsBody = this._el('rowsHost');
      if (rowsBody) rowsBody.style.display = hasAnyCheckbox ? '' : 'none';

      if (!hintEl) return;

      const isSystemMessage = ['validation', 'saving', 'error'].includes(this._hintState);
      if (isSystemMessage) return;

      if (hasPhysical && !hasNonFixedZones && !hasTextValues && !hasFiles && !hasBooleans) {
        const hasFixedZones = Array.from(this.querySelectorAll('.cpm__cb:checked')).some(cb => {
          const groupEl = cb.closest('.cpm__group');
          const isFixed = groupEl?.dataset.zoneOnly === 'true' && groupEl?.dataset.zoneChecked === 'true';
          return isFixed;
        });

        if (!hasFixedZones) {
          this._setHint('In den Warenkorb legen Artikel ohne Personalisierung', 'info');
        } else {
          if (this._hintState === 'info' || this._hintState === 'idle') {
            this._clearHint();
          }
        }
      } else {
        this._updateMissingFieldsHint();
      }
    }
      _saveRowState() {
        const state = {};

        this.querySelectorAll('.cpm__row-field-input').forEach(el => {
          const key = `${el.dataset.rowKey}__${el.dataset.group}__${el.dataset.zone}`;
          state[key] = el.value;
        });

        this.querySelectorAll('.cpm__row-field-boolean').forEach(el => {
          const key = `${el.dataset.rowKey}__${el.dataset.group}__${el.dataset.zone}`;
          state[key] = el.checked;
        });

        return state;
      }
      _restoreRowState(state) {
        if (!state) return;

        this.querySelectorAll('.cpm__row-field-input').forEach(el => {
          const key = `${el.dataset.rowKey}__${el.dataset.group}__${el.dataset.zone}`;
          if (state[key] !== undefined) {
            el.value = state[key];
          }
        });

        this.querySelectorAll('.cpm__row-field-boolean').forEach(el => {
          const key = `${el.dataset.rowKey}__${el.dataset.group}__${el.dataset.zone}`;
          if (state[key] !== undefined) {
            el.checked = state[key];
          }
        });
      }
    _calculateArticleTotal() {
      if (!this._bulkItems.length) return 0;

      const allRows = this._getCachedRows();
      const deletedPerVariant = new Map();
      allRows.forEach(r => {
        if (this._deletedRowKeys.has(r.key)) {
          const vid = String(r.variant_id);
          deletedPerVariant.set(vid, (deletedPerVariant.get(vid) || 0) + 1);
        }
      });

      let total = 0;
      this._bulkItems.forEach(item => {
        const price = Number(item.price || 0);
        const vid = String(item.variant_id);
        const qty = Math.max(0, Number(item.quantity || 0) - (deletedPerVariant.get(vid) || 0));
        total += price * qty;
      });

      return total;
    }

    _scrollToBulkComponent() {
      const bulkComponent = document.querySelector('add-to-cart-bulk');
      if (bulkComponent) {
        const offsetTop = bulkComponent.offsetTop - 20;

        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }

    _resetBulkComponent() {
      const bulkComponent = document.querySelector('add-to-cart-bulk');
      if (bulkComponent && typeof bulkComponent.reset === 'function') {
        bulkComponent.reset();
      }
    }

    _setStatus(txt) {
      const el = this._el('status');
      if (el) el.textContent = String(txt || '');
    }

    _warn(msg, obj) {
      if (obj !== undefined) console.warn('[club-print]', msg, obj);
      else console.warn('[club-print]', msg);
    }
  }

  if (!customElements.get('club-print-mini-editor')) {
    customElements.define('club-print-mini-editor', ClubPrintMiniEditor);
  }
})();

// ─── ClubPrintTests ───────────────────────────────────────────────────────────
// Run in browser console: ClubPrintTests.runAll()
window.ClubPrintTests = (() => {
  // Service items are identified by vendor, not by properties
  const isSvc = (li) => li.vendor === 'Textildruck-Service';

  function assert(cond, msg) {
    if (!cond) { console.error(`  ❌ FAIL: ${msg}`); return false; }
    console.log(`  ✅ PASS: ${msg}`); return true;
  }

  // ── Unit: service / physical classification ───────────────────────────────────
  function testItemClassification() {
    console.group('[ClubPrintTests] item classification (vendor-based)');
    let p = 0, t = 0;
    [
      [{ vendor: 'Textildruck-Service', properties: { _MpeBatch: 'b1' } }, true,  'service: vendor Textildruck-Service'],
      [{ vendor: 'Textildruck-Service', properties: {} },                   true,  'service: vendor only, no props'],
      [{ vendor: 'My Brand', properties: { _MpeBatch: 'b1' } },            false, 'physical: different vendor'],
      [{ vendor: '', properties: { _MpeBatch: 'b1' } },                    false, 'physical: empty vendor'],
      [{ vendor: 'My Brand', properties: {} },                              false, 'physical: no batch, no service vendor'],
    ].forEach(([item, exp, name]) => { t++; if (assert(isSvc(item) === exp, name)) p++; });
    console.groupEnd();
    return { p, t };
  }

  // ── Unit: _genBatchId ──────────────────────────────────────────────────────────
  function testBatchIdUniqueness() {
    console.group('[ClubPrintTests] _genBatchId uniqueness');
    let p = 0, t = 0;
    const editor = document.querySelector('club-print-mini-editor');
    if (!editor) {
      console.warn('  no club-print-mini-editor on this page — skipping');
      console.groupEnd();
      return { p: 0, t: 0 };
    }
    const ids = Array.from({ length: 20 }, () => editor._genBatchId());
    t++; if (assert(ids.every(id => typeof id === 'string' && id.length >= 8), 'all IDs are non-empty strings')) p++;
    t++; if (assert(new Set(ids).size === ids.length, `all ${ids.length} IDs are unique`)) p++;
    console.groupEnd();
    return { p, t };
  }

  // ── Unit: _buildRowsFromBulk ──────────────────────────────────────────────────
  function testBuildRowsFromBulk() {
    console.group('[ClubPrintTests] _buildRowsFromBulk');
    let p = 0, t = 0;
    const editor = document.querySelector('club-print-mini-editor');
    if (!editor) {
      console.warn('  no club-print-mini-editor on this page — skipping');
      console.groupEnd();
      return { p: 0, t: 0 };
    }

    const mockBulk = [
      { variant_id: '111', quantity: 2, color: 'Red', size: 'M', product_title: 'Test Jersey' },
      { variant_id: '222', quantity: 1, color: 'Blue', size: 'L', product_title: 'Test Jersey' },
    ];

    const rows = editor._buildRowsFromBulk(mockBulk);
    t++; if (assert(rows.length === 3, 'qty 2+1 → 3 rows')) p++;
    t++; if (assert(rows[0].key === '111#1', 'row 0 key: variantId#idx')) p++;
    t++; if (assert(rows[1].key === '111#2', 'row 1 key: variantId#idx')) p++;
    t++; if (assert(rows[2].key === '222#3', 'row 2 key: variantId#idx')) p++;
    t++; if (assert(rows.every(r => r.variant_id && r.title), 'all rows have variant_id and title')) p++;

    // Edge: empty bulk
    const emptyRows = editor._buildRowsFromBulk([]);
    t++; if (assert(emptyRows.length === 0, 'empty bulk → no rows')) p++;

    // Edge: qty = 0
    const zeroQtyRows = editor._buildRowsFromBulk([{ variant_id: '333', quantity: 0, color: '', size: '', product_title: '' }]);
    t++; if (assert(zeroQtyRows.length === 0, 'qty=0 → no rows')) p++;

    console.groupEnd();
    return { p, t };
  }

  // ── Unit: unified _MpeBatch is stored in localStorage after apply ─────────────
  function testLocalStorageBatchId() {
    console.group('[ClubPrintTests] localStorage batch ID');
    let p = 0, t = 0;
    const stored = localStorage.getItem('mpe:lastBatchId');
    if (stored) {
      t++; if (assert(typeof stored === 'string' && stored.length >= 8, `stored batchId is valid string: "${stored}"`)) p++;
    } else {
      console.info('  no batchId in localStorage yet (apply has not been called)');
    }
    console.groupEnd();
    return { p, t };
  }

  // ── Live: cart batch integrity ────────────────────────────────────────────────
  async function testLiveCartIntegrity() {
    console.group('[ClubPrintTests] live cart — batch integrity');
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(r => r.json());

      const all = cart.items || [];
      const svcs = all.filter(li => isSvc(li));
      const phys = all.filter(li => !isSvc(li));

      console.log(`  Cart: ${all.length} total | ${phys.length} physical | ${svcs.length} service`);

      // New-style service items should only have _MpeBatch (no extra metadata)
      const svcsWithExtraProps = svcs.filter(li => {
        const p = li.properties || {};
        return p._LinkedToHandle || p._IsService || p._ServiceIndex;
      });
      if (svcsWithExtraProps.length) {
        console.warn(`  ⚠️ ${svcsWithExtraProps.length} service item(s) have legacy properties (old-style):`,
          svcsWithExtraProps.map(li => ({ title: li.title, props: li.properties })));
      }

      // Physical items with _MpeBatch should have personalization data
      const physWithBatch = phys.filter(li => li.properties?._MpeBatch);
      const physMissingData = physWithBatch.filter(li => !li.properties?.['#1']);
      if (physMissingData.length) {
        console.warn(`  ⚠️ ${physMissingData.length} physical batch item(s) missing Druck properties:`,
          physMissingData.map(li => li.title));
      }

      // Group by _MpeBatch
      const byBatch = new Map();
      all.forEach(li => {
        const b = li.properties?._MpeBatch;
        if (!b) return;
        if (!byBatch.has(b)) byBatch.set(b, { phys: [], svc: [] });
        isSvc(li) ? byBatch.get(b).svc.push(li) : byBatch.get(b).phys.push(li);
      });

      if (byBatch.size) {
        console.group('  Batches:');
        byBatch.forEach((g, b) => {
          const status = g.phys.length > 0 && g.svc.length > 0 ? '✅ complete'
            : g.svc.length > 0 && g.phys.length === 0 ? '⚠️ orphaned services'
            : 'ℹ️ physical only (no print service)';
          console.log(`  ${b}: ${g.phys.length} physical, ${g.svc.length} service — ${status}`);
        });
        console.groupEnd();
      } else {
        console.log('  No batched items in cart');
      }
    } catch (e) {
      console.error('  Cart fetch failed:', e);
    }
    console.groupEnd();
  }

  // ── runAll ────────────────────────────────────────────────────────────────────
  async function runAll() {
    console.group('%c[ClubPrintTests] Running all tests', 'font-weight:700;color:#2e7d32;font-size:13px;');
    let p = 0, t = 0;
    const r1 = testItemClassification();  p += r1.p; t += r1.t;
    const r2 = testBatchIdUniqueness();   p += r2.p; t += r2.t;
    const r3 = testBuildRowsFromBulk();   p += r3.p; t += r3.t;
    const r4 = testLocalStorageBatchId(); p += r4.p; t += r4.t;
    await testLiveCartIntegrity();
    const ok = p === t;
    console.log(`%c  Result: ${p}/${t} unit tests passed`, ok ? 'color:green;font-weight:700' : 'color:red;font-weight:700');
    if (!ok) console.error('  Some tests FAILED — check output above');
    console.groupEnd();
    return ok;
  }

  return { runAll, testItemClassification, testBatchIdUniqueness, testBuildRowsFromBulk, testLocalStorageBatchId, testLiveCartIntegrity };
})();